import { GeneratedContent, LabStep } from '../types/content';

const emptyStep = (order: number): LabStep => ({
  title: `Step ${order}`,
  instruction: '',
  expectedResult: '',
  codeSample: '',
  language: '',
  notes: '',
  displayOrder: order,
});

const blockType = (type?: string) => (type || 'paragraph').replace(/[_-]/g, '').toLowerCase();

const stripStepPrefix = (heading: string) => heading.replace(/^\s*step\s*\d+\s*[:.\-–—)]\s*/i, '').trim() || heading;

const joinLines = (values?: string[]) => (values || []).map((line) => line.trim()).filter(Boolean).join('\n');

const proseFromBlocks = (blocks: { type: string; content?: string }[]) =>
  blocks.filter((block) => blockType(block.type) !== 'code').map((block) => (block.content || '').trim()).filter(Boolean);

export function labDraftFromGenerated(content: GeneratedContent) {
  const generatedSteps = (content.steps || []).filter((step) => (step.title || step.instruction || step.codeSample || '').trim());
  const sections = content.sections || [];
  const prereqSection = sections.find((section) => /prerequisite/i.test(section.heading));
  const practiceSections = sections.filter((section) => section !== prereqSection);

  let steps: LabStep[];
  if (generatedSteps.length) {
    steps = generatedSteps.map((step, index) => ({
      title: stripStepPrefix(step.title || `Step ${index + 1}`),
      instruction: step.instruction || '',
      codeSample: step.codeSample || '',
      language: step.language || '',
      expectedResult: step.expectedResult || '',
      notes: step.notes || '',
      displayOrder: index + 1,
    }));
  } else if (practiceSections.length) {
    steps = practiceSections.map((section, index) => {
      const blocks = section.blocks || [];
      const code = blocks.find((block) => blockType(block.type) === 'code');
      const prose = proseFromBlocks(blocks);
      const expectedResult = code && prose.length > 1 ? prose[prose.length - 1] : '';
      const instruction = code && prose.length > 1 ? prose.slice(0, -1).join('\n\n') : prose.join('\n\n');
      return {
        title: stripStepPrefix(section.heading || `Step ${index + 1}`),
        instruction,
        codeSample: code?.content || '',
        language: code?.language || '',
        expectedResult,
        notes: '',
        displayOrder: index + 1,
      };
    });
  } else {
    steps = (content.handsOn?.steps || []).map((block, index) => ({
      title: `Step ${index + 1}`,
      instruction: block.content || '',
      expectedResult: content.handsOn?.expectedOutcome || '',
      codeSample: '',
      language: '',
      notes: '',
      displayOrder: index + 1,
    }));
  }

  const prereqFromSection = prereqSection ? proseFromBlocks(prereqSection.blocks || []).join('\n') : '';

  return {
    name: (content.title || '').trim(),
    subtitle: (content.subtitle || '').trim(),
    description: (content.objective || '').trim(),
    duration: (content.duration || '').trim(),
    prerequisites: (content.prerequisites || prereqFromSection).trim(),
    outcomes: joinLines(content.learningOutcomes?.length ? content.learningOutcomes : content.keyTakeaways),
    steps: steps.length ? steps : [emptyStep(1)],
  };
}
